import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaBan, FaCheck } from "react-icons/fa";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DataTable from "../../components/DataTable";
import {
  acceptDriver,
  blockDriver,
  fetchRejectedDrivers,
  fetchWaitingDrivers,
  rejectDriver,
} from "../../services/drivers";
import TableTitle from "../../components/TableTitle";
import {
  ButtonRow,
  CancelButton,
  CheckIcon,
  CloseIcon,
  ConfirmButton,
  Icons,
  ModalContent,
  ModalOverlay,
  ModalTitle,
  TablePopup,
  TextArea,
} from "./style";
import Avatar from "../../components/Avatar";
import Spinner from "../../components/Spinner";
import { PopupButton } from "../../components/Navbar/style";
import ImagePopup from "../../components/ImagePopup";

const columns = [
  "ID",
  "الاسم",
  "الصورة",
  "تصنيف المركبة",
  "نوع السيارة",
  "رقم اللوحة",
  "رقم الهاتف",
  "اللون",
  "البطاقة الشخصية",
  "رخصة القيادة",
  " ",
];

const Requests = () => {
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showSettingPopup, setShowSettingPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    imageUrl: "",
    title: "",
  });

  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowSettingPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { data: waiting = [], isLoading: waitingLoading } = useQuery({
    queryKey: ["drivers", "waiting"],
    queryFn: fetchWaitingDrivers,
  });

  const { data: rejected = [], isLoading: rejectedLoading } = useQuery({
    queryKey: ["drivers", "rejected"],
    queryFn: fetchRejectedDrivers,
  });

  const qc = useQueryClient();

  const acceptMut = useMutation({
    mutationFn: acceptDriver,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("✅ تم قبول السائق");
    },
    onError: () => toast.error("❌ فشل في قبول السائق"),
  });

  const rejectMut = useMutation({
    mutationFn: rejectDriver,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("🚫 تم رفض السائق");
    },
    onError: () => toast.error("❌ فشل في رفض السائق"),
  });

  const blockMut = useMutation({
    mutationFn: ({ id, isBlocked }) => blockDriver(id, isBlocked),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("🚫 تم حظر السائق بنجاح ");
    },
    onError: () => toast.error("❌ فشل في حظر السائق"),
  });

  const renderImagePopup = (img, title) => (
    <span
      style={{
        color: "#16706aff",
        cursor: "pointer",
      }}
      onClick={(e) => {
        e.stopPropagation();
        setImagePopup({
          isOpen: true,
          imageUrl: img,
          title: title,
        });
      }}
    >
      عرض الصورة
    </span>
  );

  const mapRows = useCallback(
    (list, withActions = false) =>
      list.map((d, id) => {
        const u = d.user ?? {};
        const v = d.vehicle ?? {};

        const actionCells = withActions
          ? [
              <Icons key="actions">
                <CheckIcon
                  key="accept"
                  title="قبول"
                  onClick={() => acceptMut.mutate(d._id)}
                />
                <CloseIcon
                  key="reject"
                  title="رفض"
                  onClick={() => {
                    setSelectedDriverId(d._id);
                    setShowRejectPopup(true);
                  }}
                />
              </Icons>,
            ]
          : [
              <BsThreeDotsVertical
                key="dots"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  e.stopPropagation();
                  if (showSettingPopup && selectedDriverId === d._id) {
                    setShowSettingPopup(false);
                    setSelectedDriverId(null);
                    return;
                  }

                  setPopupPosition({
                    top: rect.bottom + window.scrollY + 25,
                    right: window.innerWidth - rect.right - 30,
                  });

                  setSelectedDriverId(d._id);
                  setShowSettingPopup(true);
                }}
              />,
            ];

        return [
          id + 1,
          u.name ?? "—",
          <Avatar key="avatar" img={d.user?.profile_img} name={u.name} />,
          v.car_class ?? "—",
          v.brand ?? "—",
          v.license_plate ?? "—",
          u.phone_number ?? "—",
          v.color ?? "—",
          renderImagePopup(d.id_card_img, "البطاقة الشخصية"),
          renderImagePopup(d.license_img, "رخصة القيادة"),
          ...actionCells,
          `/driver-profile/${d._id}`,
        ];
      }),
    [acceptMut, showSettingPopup, selectedDriverId]
  );

  const waitingRows = useMemo(() => mapRows(waiting, true), [mapRows, waiting]);
  const rejectedRows = useMemo(() => mapRows(rejected, false), [mapRows, rejected]);

  if (waitingLoading || rejectedLoading) return <Spinner size="60px" />;

  return (
    <>
      {showRejectPopup && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>سبب الرفض</ModalTitle>
            <TextArea
              rows="4"
              maxLength={255}
              placeholder="اكتب سبب الرفض هنا..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <ButtonRow>
              <CancelButton
                onClick={() => {
                  setShowRejectPopup(false);
                  setRejectReason("");
                  setSelectedDriverId(null);
                }}
              >
                إلغاء
              </CancelButton>
              <ConfirmButton
                onClick={() => {
                  if (!rejectReason.trim()) {
                    toast.error("يرجى إدخال سبب الرفض");
                    return;
                  }

                  rejectMut.mutate({
                    id: selectedDriverId,
                    reason: rejectReason,
                  });
                  setShowRejectPopup(false);
                  setRejectReason("");
                  setSelectedDriverId(null);
                }}
              >
                رفض
              </ConfirmButton>
            </ButtonRow>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Image Popup Component */}
      <ImagePopup
        isOpen={imagePopup.isOpen}
        imageUrl={imagePopup.imageUrl}
        title={imagePopup.title}
        onClose={() =>
          setImagePopup({ isOpen: false, imageUrl: "", title: "" })
        }
      />

      {showSettingPopup && (
        <TablePopup
          ref={popupRef}
          style={{
            position: "absolute",
            top: `${popupPosition.top}px`,
            right: `${popupPosition.right}px`,
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            padding: "4px 0",
            zIndex: 1000,
            minWidth: "180px",
          }}
        >
          <PopupButton
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 15px",
              textAlign: "right",
              width: "100%",
              border: "none",
              background: "transparent",
              color: "#5cb85c",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#e9f7ee")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
            onClick={() => {
              acceptMut.mutate(selectedDriverId);
              setShowSettingPopup(false);
            }}
          >
            <FaCheck /> قبول السائق
          </PopupButton>
          <div style={{ height: "1px", background: "#eee" }} />
          <PopupButton
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 15px",
              textAlign: "right",
              width: "100%",
              border: "none",
              background: "transparent",
              color: "#d9534f",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fceaea")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
            onClick={() => {
              blockMut.mutate({ id: selectedDriverId, isBlocked: true });
              setShowSettingPopup(false);
            }}
          >
            <FaBan /> حظر السائق
          </PopupButton>
        </TablePopup>
      )}

      {!waitingLoading && waiting.length > 0 && (
        <>
          <TableTitle title="الطلبات قبول السائقين" />
          <DataTable columns={columns} data={waitingRows} />
        </>
      )}
      {waiting.length === 0 && (
        <div style={{ marginTop: "10px" }}>
          <TableTitle title="الطلبات قبول السائقين" />
          <div
            style={{
              textAlign: "center",
              padding: "30px 20px",
              marginTop: "10px",
              borderRadius: "10px",
            }}
          >
            <p
              style={{
                fontSize: "18px",
                color: "#777",
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              🚗 لا يوجد سائقين بقائمة الانتظار
            </p>
          </div>
        </div>
      )}

      {/* Rejected Drivers Table */}
      {!rejectedLoading && rejected.length > 0 && (
        <>
          <TableTitle title="الطلبات المرفوضة" />
          <DataTable columns={columns} data={rejectedRows} />
        </>
      )}
      {rejected.length === 0 && (
        <div style={{ marginTop: "10px" }}>
          <TableTitle title="الطلبات المرفوضة" />
          <div
            style={{
              textAlign: "center",
              padding: "30px 20px",
              marginTop: "10px",
              borderRadius: "10px",
            }}
          >
            <p
              style={{
                fontSize: "18px",
                color: "#777",
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              🚗 لا يوجد سائقين بقائمة الطلبات المرفوضة
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Requests;